import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Image, Send, Mail, Phone, X, Loader2, TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle2, ArrowRight, Copy, Check, ExternalLink, History, RefreshCw, Calendar, ChevronDown, ChevronUp, Users, ClipboardPaste, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MobileNav from '../components/MobileNav';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientProgressPage({ clientId = null, clientData = null, onClose = null }) {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareableLink, setShareableLink] = useState(null);
  
  // Client contact info
  const [clientName, setClientName] = useState(clientData?.name || '');
  const [clientEmail, setClientEmail] = useState(clientData?.email || '');
  const [clientPhone, setClientPhone] = useState(clientData?.phone || '');
  const [sendMethod, setSendMethod] = useState('both');

  // Paste client info
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parsing, setParsing] = useState(false);

  // Client Progress Log
  const [showClientLog, setShowClientLog] = useState(false);
  const [progressClients, setProgressClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  // Progress History
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const newImages = acceptedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
    setAnalysisResult(null);
    setShareableLink(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic'] },
    multiple: true
  });

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
    setAnalysisResult(null);
    setShareableLink(null);
  };

  // Fetch all clients who have received progress updates
  const fetchProgressClients = async () => {
    setLoadingClients(true);
    try {
      const response = await axios.get(`${API}/api/client-progress/clients`);
      if (response.data.success) {
        setProgressClients(response.data.clients || []);
      }
    } catch (error) {
      console.error('Error fetching progress clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  // Load clients on mount
  useEffect(() => {
    fetchProgressClients();
  }, []);

  // Parse pasted client info
  const handleParseClientInfo = async () => {
    if (!pasteText.trim()) {
      toast.error('Please paste client information');
      return;
    }
    
    setParsing(true);
    try {
      const response = await axios.post(`${API}/api/client-progress/parse-info`, {
        raw_text: pasteText
      });
      
      if (response.data.success) {
        const parsed = response.data.parsed;
        if (parsed.name) setClientName(parsed.name);
        if (parsed.email) setClientEmail(parsed.email);
        if (parsed.phone) setClientPhone(parsed.phone);
        
        setShowPasteModal(false);
        setPasteText('');
        
        if (parsed.name || parsed.email || parsed.phone) {
          toast.success('Client info extracted successfully!');
        } else {
          toast.warning('Could not extract client info. Please enter manually.');
        }
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Failed to parse client info');
    } finally {
      setParsing(false);
    }
  };

  // Select client from log
  const selectClientFromLog = (client) => {
    setClientName(client.name || '');
    setClientEmail(client.email || '');
    setClientPhone(client.phone || '');
    setShowClientLog(false);
    toast.success(`Selected ${client.name || client.email}`);
    
    // Fetch their history
    if (client.email) {
      fetchHistoryByEmail(client.email);
    }
  };

  // Fetch progress history by email
  const fetchHistoryByEmail = async (email) => {
    if (!email) return;
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/api/client-progress/history`, {
        params: { email }
      });
      if (response.data.success) {
        setHistory(response.data.history || []);
        if (response.data.history?.length > 0) {
          setShowHistory(true);
        }
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Auto-fetch history when email changes
  useEffect(() => {
    if (clientEmail && clientEmail.includes('@')) {
      const timer = setTimeout(() => {
        fetchHistoryByEmail(clientEmail);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setHistory([]);
      setShowHistory(false);
    }
  }, [clientEmail]);

  // Analyze screenshots
  const handleAnalyze = async () => {
    if (uploadedImages.length === 0) {
      toast.error('Please upload at least one screenshot');
      return;
    }

    setAnalyzing(true);
    try {
      const imagePromises = uploadedImages.map(img => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(img.file);
        });
      });

      const base64Images = await Promise.all(imagePromises);

      const response = await axios.post(`${API}/api/client-progress/analyze`, {
        images: base64Images,
        client_name: clientName || 'Client',
        client_id: clientId
      });

      if (response.data.success) {
        setAnalysisResult(response.data.analysis);
        toast.success('Analysis complete!');
      } else {
        toast.error(response.data.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze screenshots');
    } finally {
      setAnalyzing(false);
    }
  };

  // Send progress update
  const handleSend = async () => {
    if (!analysisResult) {
      toast.error('Please analyze the screenshots first');
      return;
    }
    if (!clientEmail && !clientPhone) {
      toast.error('Please enter client email or phone number');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/api/client-progress/send`, {
        client_name: clientName,
        client_email: clientEmail,
        client_phone: clientPhone,
        send_method: sendMethod,
        analysis: analysisResult,
        client_id: clientId
      });

      if (response.data.success) {
        setShareableLink(response.data.shareable_link);
        toast.success(`Progress update sent to ${clientName || 'client'}!`);
        // Refresh history and client list
        if (clientEmail) fetchHistoryByEmail(clientEmail);
        fetchProgressClients();
      } else {
        toast.error(response.data.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error('Failed to send progress update');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (shareableLink) {
      await navigator.clipboard.writeText(shareableLink);
      setLinkCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleNewAnalysis = () => {
    setUploadedImages([]);
    setAnalysisResult(null);
    setShareableLink(null);
  };

  const formatScore = (score) => {
    if (!score) return 'N/A';
    if (typeof score === 'object') {
      return Object.entries(score).map(([bureau, val]) => `${bureau}: ${val}`).join(', ');
    }
    return score;
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6">
      {/* Mobile Navigation Menu */}
      <MobileNav />
      
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              Client Progress
            </h1>
            <p className="text-zinc-400 mt-1">Upload screenshots to analyze and share progress with your client</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowClientLog(!showClientLog)}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                showClientLog 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
              }`}
            >
              <Users className="w-4 h-4" />
              Client Log ({progressClients.length})
            </button>
            {onClose && (
              <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            )}
          </div>
        </div>

        {/* Client Progress Log Panel */}
        {showClientLog && (
          <div className="bg-zinc-900 border border-teal-500/30 rounded-xl p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-teal-400 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Client Progress Log
              </h2>
              <button 
                onClick={fetchProgressClients}
                disabled={loadingClients}
                className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
              >
                <RefreshCw className={`w-4 h-4 ${loadingClients ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            
            {loadingClients ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-teal-400 animate-spin mx-auto" />
                <p className="text-zinc-400 mt-2">Loading clients...</p>
              </div>
            ) : progressClients.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No clients have received progress updates yet.</p>
                <p className="text-sm mt-1">Send your first update to see clients here!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto">
                {progressClients.map((client, index) => (
                  <button
                    key={client.email || index}
                    onClick={() => selectClientFromLog(client)}
                    className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-left transition-all border border-zinc-700 hover:border-teal-500/50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white truncate">
                          {client.name || 'Unknown'}
                        </p>
                        <p className="text-sm text-zinc-400 truncate">{client.email}</p>
                        {client.phone && (
                          <p className="text-xs text-zinc-500">{client.phone}</p>
                        )}
                      </div>
                      <div className="text-right ml-2">
                        <span className="text-xs bg-teal-500/20 text-teal-400 px-2 py-1 rounded">
                          {client.update_count} update{client.update_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {client.latest_scores && (
                      <p className="text-xs text-zinc-500 mt-2 truncate">
                        Latest: {formatScore(client.latest_scores)}
                      </p>
                    )}
                    <p className="text-xs text-zinc-600 mt-1">
                      {new Date(client.latest_update).toLocaleDateString()}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Client Info Section */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-teal-400" />
              Client Information
            </h2>
            <div className="flex items-center gap-2">
              {/* Paste Client Info Button */}
              <button
                onClick={() => setShowPasteModal(true)}
                className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-medium flex items-center gap-2 transition-all"
              >
                <ClipboardPaste className="w-4 h-4" />
                Paste Info
              </button>
              {history.length > 0 && (
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
                >
                  <History className="w-4 h-4" />
                  {history.length} Update{history.length !== 1 ? 's' : ''}
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-teal-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Email Address</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="client@email.com"
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-teal-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Phone Number</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-teal-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Progress History (Collapsible) */}
          {showHistory && history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Progress History for {clientName || clientEmail}
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((report, index) => {
                  const isExpanded = expandedHistory[index];
                  const scores = report.analysis?.scores;
                  const updates = report.analysis?.updates || [];
                  
                  return (
                    <div key={report.share_id} className="bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                      <button
                        onClick={() => setExpandedHistory(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="w-full p-3 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-zinc-500" />
                          <span className="text-sm text-zinc-300">
                            {new Date(report.created_at).toLocaleDateString('en-US', {
                              year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </span>
                          {scores?.current && (
                            <span className="text-xs text-zinc-500">{formatScore(scores.current)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {updates.filter(u => u.type === 'positive').length > 0 && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                              {updates.filter(u => u.type === 'positive').length} positive
                            </span>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-0 border-t border-zinc-700/50">
                          {report.analysis?.explanation && (
                            <p className="text-xs text-zinc-400 mt-2 line-clamp-3">{report.analysis.explanation}</p>
                          )}
                          {updates.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {updates.map((update, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                  {update.type === 'positive' ? (
                                    <CheckCircle2 className="w-3 h-3 text-green-400" />
                                  ) : (
                                    <AlertCircle className="w-3 h-3 text-zinc-400" />
                                  )}
                                  <span className={update.type === 'positive' ? 'text-green-400' : 'text-zinc-400'}>
                                    {update.title}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          <a
                            href={`${window.location.origin}/progress/${report.share_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-teal-400 hover:text-teal-300"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Full Report
                          </a>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 md:p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-teal-400" />
            Upload Screenshots
          </h2>
          <p className="text-sm text-zinc-400 mb-4">
            Upload screenshots from Credit Karma, Experian, or any credit monitoring app showing score changes and account updates.
          </p>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragActive ? 'border-teal-500 bg-teal-500/10' : 'border-zinc-600 hover:border-teal-500/50 hover:bg-zinc-800/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-teal-400' : 'text-zinc-500'}`} />
            {isDragActive ? (
              <p className="text-teal-400 font-medium">Drop the screenshots here...</p>
            ) : (
              <>
                <p className="text-white font-medium">Drag & drop screenshots here</p>
                <p className="text-zinc-500 text-sm mt-1">or click to select files</p>
              </>
            )}
          </div>

          {uploadedImages.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-medium text-zinc-300">{uploadedImages.length} screenshot(s) ready</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {uploadedImages.map((img, index) => (
                  <div key={index} className="relative group">
                    <img src={img.preview} alt={`Screenshot ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-zinc-600" />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleAnalyze}
            disabled={uploadedImages.length === 0 || analyzing}
            className="mt-4 w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-zinc-700 disabled:to-zinc-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all"
          >
            {analyzing ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Analyzing with AI...</>
            ) : (
              <><Image className="w-5 h-5" />Analyze Screenshots</>
            )}
          </button>
        </div>

        {/* Analysis Results */}
        {analysisResult && (
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-4 md:p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                Analysis Results
              </h2>
              <button onClick={handleNewAnalysis} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1">
                <RefreshCw className="w-4 h-4" />New Analysis
              </button>
            </div>

            {analysisResult.scores && (
              <div className="bg-zinc-800 rounded-xl p-4">
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Credit Score Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {analysisResult.scores.previous && (
                    <div className="text-center p-3 bg-zinc-700/50 rounded-lg">
                      <p className="text-xs text-zinc-400">Previous Score</p>
                      <p className="text-2xl font-bold text-zinc-300">{formatScore(analysisResult.scores.previous)}</p>
                    </div>
                  )}
                  {analysisResult.scores.current && (
                    <div className="text-center p-3 bg-zinc-700/50 rounded-lg">
                      <p className="text-xs text-zinc-400">Current Score</p>
                      <p className="text-2xl font-bold text-white">{formatScore(analysisResult.scores.current)}</p>
                    </div>
                  )}
                  {analysisResult.scores.change !== undefined && (
                    <div className={`text-center p-3 rounded-lg ${
                      (typeof analysisResult.scores.change === 'object' 
                        ? Object.values(analysisResult.scores.change).some(v => v > 0)
                        : analysisResult.scores.change > 0)
                        ? 'bg-green-500/20' : 'bg-zinc-700/50'
                    }`}>
                      <p className="text-xs text-zinc-400">Change</p>
                      <p className="text-2xl font-bold text-green-400 flex items-center justify-center gap-1">
                        <TrendingUp className="w-5 h-5" />
                        {typeof analysisResult.scores.change === 'object' 
                          ? Object.entries(analysisResult.scores.change).map(([b, v]) => `${b}: +${v}`).join(', ')
                          : `+${analysisResult.scores.change}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 border border-teal-500/30 rounded-xl p-4">
              <h3 className="text-sm font-medium text-teal-400 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />What This Means For You
              </h3>
              <div className="text-zinc-200 whitespace-pre-wrap text-sm leading-relaxed">{analysisResult.explanation}</div>
            </div>

            {analysisResult.updates && analysisResult.updates.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-zinc-400 mb-3">Account Updates Detected</h3>
                <div className="space-y-2">
                  {analysisResult.updates.map((update, index) => (
                    <div key={index} className={`p-3 rounded-lg flex items-start gap-3 ${
                      update.type === 'positive' ? 'bg-green-500/10 border border-green-500/30' 
                        : update.type === 'negative' ? 'bg-red-500/10 border border-red-500/30'
                        : 'bg-zinc-800 border border-zinc-600'
                    }`}>
                      {update.type === 'positive' ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        : update.type === 'negative' ? <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        : <ArrowRight className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-0.5" />}
                      <div>
                        <p className={`font-medium ${update.type === 'positive' ? 'text-green-400' : update.type === 'negative' ? 'text-red-400' : 'text-white'}`}>{update.title}</p>
                        <p className="text-sm text-zinc-400">{update.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Send Options */}
            <div className="border-t border-zinc-700 pt-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Send To Client Via:</h3>
              <div className="flex gap-2 mb-4">
                <button onClick={() => setSendMethod('email')} className={`flex-1 py-2 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${sendMethod === 'email' ? 'bg-teal-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                  <Mail className="w-4 h-4" />Email Only
                </button>
              </div>

              <button
                onClick={handleSend}
                disabled={sending || (!clientEmail && !clientPhone)}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-zinc-700 disabled:to-zinc-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all"
              >
                {sending ? <><Loader2 className="w-5 h-5 animate-spin" />Sending...</> : <><Send className="w-5 h-5" />Send Progress Update to Client</>}
              </button>
              
              {history.length > 0 && (
                <p className="text-xs text-zinc-500 mt-2 text-center">
                  This client has {history.length} previous update(s). Full history will be included in the email.
                </p>
              )}
            </div>

            {shareableLink && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h3 className="text-sm font-medium text-green-400 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />Progress Update Sent!
                </h3>
                <p className="text-sm text-zinc-300 mb-3">Share this link with your client:</p>
                <div className="flex items-center gap-2">
                  <input type="text" value={shareableLink} readOnly className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-teal-400 text-sm font-mono" />
                  <button onClick={copyLink} className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${linkCopied ? 'bg-green-600 text-white' : 'bg-zinc-700 text-white hover:bg-zinc-600'}`}>
                    {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{linkCopied ? 'Copied!' : 'Copy'}
                  </button>
                  <a href={shareableLink} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paste Client Info Modal */}
        {showPasteModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-lg shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <ClipboardPaste className="w-5 h-5 text-purple-400" />
                  Paste Client Info
                </h3>
                <button onClick={() => { setShowPasteModal(false); setPasteText(''); }} className="text-zinc-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-sm text-zinc-400 mb-4">
                Paste any text containing the client's information. We'll automatically extract their name, email, and phone number.
              </p>
              
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`Example:\nJohn Smith\njohn@email.com\n(555) 123-4567\n\nOr paste from an email, spreadsheet, etc.`}
                className="w-full h-40 px-4 py-3 bg-zinc-800 border border-zinc-600 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none resize-none font-mono text-sm"
                autoFocus
              />
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowPasteModal(false); setPasteText(''); }}
                  className="flex-1 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleParseClientInfo}
                  disabled={!pasteText.trim() || parsing}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-zinc-600 disabled:to-zinc-700 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  {parsing ? <><Loader2 className="w-4 h-4 animate-spin" />Extracting...</> : <><User className="w-4 h-4" />Extract Info</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
